import { c as _c } from "react/compiler-runtime";
import type { UserConsent } from '@onefootprint/request-types';
import { Divider } from '@onefootprint/ui';
import type { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { PoweredByVerified } from '@/idv2/components/powered-by-verified';
const link = (href: string) => <a href={href} rel="noopener noreferrer" target="_blank" className="text-accent">
    {' '}
  </a>;
const LINKS = {
  termsLink: link('https://www.onefootprint.com/terms-of-service'),
  privacyPolicyLink: link('https://www.onefootprint.com/privacy-policy'),
  linkButton: link('https://verified.inc')
};

/**
 * The one sentence the footer shows. When both consents are required they merge into a single
 * sentence rather than two paragraphs that each open with "By continuing, I agree".
 */
const getSentence = (requiredConsent: UserConsent[]) => {
  const verified = requiredConsent.find(c => c.kind === 'verified_prefill');
  const hasTerms = requiredConsent.some(c => c.kind === 'terms_and_conditions');
  const collectsSsn = !!verified?.collectsSsn;
  if (verified) return {
    kind: hasTerms ? 'both' as const : 'verified' as const,
    collectsSsn
  };
  return hasTerms ? {
    kind: 'terms' as const,
    collectsSsn
  } : undefined;
};

/** The only markup our copy carries. Matching them by name beats stripping tags heuristically. */
const COPY_TAGS = new RegExp(`</?(${Object.keys(LINKS).join('|')})>`, 'g');

/** The Verified copy names what the flow collects, so the SSN clause is interpolated in. */
const getSsn = (t: TFunction<'idv2'>, collectsSsn: boolean) => collectsSsn ? t($ => $.identify.otp.verifiedPrefill.ssn) : '';

/** The plain text of the sentence displayed, which is what we store on every consent log. */
export const getConsentContent = (t: TFunction<'idv2'>, requiredConsent: UserConsent[]) => {
  const sentence = getSentence(requiredConsent);
  const ssn = getSsn(t, !!sentence?.collectsSsn);
  const text = sentence?.kind === 'both' ? t($ => $.identify.otp.verifiedPrefill.termsAndDisclaimer, {
    ssn
  }) : sentence?.kind === 'verified' ? t($ => $.identify.otp.verifiedPrefill.disclaimer, {
    ssn
  }) : t($ => $.identify.otp.legalFooter);
  return text.replace(COPY_TAGS, '');
};

/** The disclaimer the user accepts by continuing from the screen this sits under. */
export const ConsentFooter = t0 => {
  const $ = _c(31);
  const {
    requiredConsent
  } = t0;
  const {
    t
  } = useTranslation("idv2");
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== requiredConsent || $[1] !== t) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const sentence = getSentence(requiredConsent);
      if (!sentence) {
        t9 = null;
        break bb0;
      }
      t7 = "flex flex-col items-center justify-center gap-6 mt-6";
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = <Divider />;
        $[12] = t8;
      } else {
        t8 = $[12];
      }
      t5 = "flex flex-col gap-4";
      t6 = sentence.kind !== "terms" && <PoweredByVerified />;
      t4 = "text-caption-2 text-tertiary text-center";
      T0 = Trans;
      t2 = "idv2";
      t3 = sentence.kind === "both" ? _temp : sentence.kind === "verified" ? _temp2 : _temp3;
      t1 = getSsn(t, sentence.collectsSsn);
    }
    $[0] = requiredConsent;
    $[1] = t;
    $[2] = T0;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    T0 = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
    t9 = $[11];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[13] !== t1) {
    t10 = {
      ssn: t1
    };
    $[13] = t1;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== T0 || $[16] !== t10 || $[17] !== t2 || $[18] !== t3) {
    t11 = <T0 ns={t2} i18nKey={t3} values={t10} components={LINKS} />;
    $[15] = T0;
    $[16] = t10;
    $[17] = t2;
    $[18] = t3;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t4) {
    t12 = <p className={t4}>{t11}</p>;
    $[20] = t11;
    $[21] = t4;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t5 || $[25] !== t6) {
    t13 = <div className={t5}>{t6}{t12}</div>;
    $[23] = t12;
    $[24] = t5;
    $[25] = t6;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13 || $[28] !== t7 || $[29] !== t8) {
    t14 = <div className={t7}>{t8}{t13}</div>;
    $[27] = t13;
    $[28] = t7;
    $[29] = t8;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
};
function _temp($) {
  return $.identify.otp.verifiedPrefill.termsAndDisclaimer;
}
function _temp2($_0) {
  return $_0.identify.otp.verifiedPrefill.disclaimer;
}
function _temp3($_1) {
  return $_1.identify.otp.legalFooter;
}